// D:\brickbucket_v3.1\Brickbucket_Frontend_V3.1\src\ConsumerApp\api\apiClient.js

const RAW_BASE_URL =
  process.env.REACT_APP_BRICKBUCKET_CONSUMER ||
  process.env.REACT_APP_FORE_NOTIFY ;

// Strip any trailing slash so `${BASE_URL}${endpoint}` never produces double slashes
const BASE_URL = RAW_BASE_URL.replace(/\/+$/, '');

/**
 * Universal request wrapper around browser fetch
 */
const request = async (endpoint, { method = 'GET', body, headers = {}, timeout = 15000, ...customConfig } = {}) => {
  // 1. Retrieve session token
  let token = null;
  try {
    const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
    if (stored) {
      const parsed = JSON.parse(stored);
      token = parsed?.token || parsed?.access_token || parsed?.jwt;
    }
  } catch (e) {
    console.error('Error reading auth token:', e);
  }

  // 2. Build headers
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  // 3. Setup timeout controller
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const config = {
    method,
    headers: defaultHeaders,
    signal: controller.signal,
    ...customConfig,
  };

  if (body) {
    config.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // Ensure clean path join
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${formattedEndpoint}`;

  try {
    const response = await fetch(url, config);
    clearTimeout(timeoutId);

    // 4. Handle 401 Unauthorized globally
    if (response.status === 401) {
      sessionStorage.removeItem('userData');
      localStorage.removeItem('userData');
      if (!window.location.pathname.startsWith('/consumer')) {
        window.location.href = '/consumer';
      }
    }

    // 5. Safe JSON parse
    const contentType = response.headers.get('content-type');
    const data = contentType && contentType.includes('application/json')
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);

    if (!response.ok) {
      throw data || new Error(`Request failed with status ${response.status}`);
    }

    // 6. Return standard unwrapped payload
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
};

export const apiClient = {
  get: (endpoint, options = {}) => request(endpoint, { method: 'GET', ...options }),
  post: (endpoint, body, options = {}) => request(endpoint, { method: 'POST', body, ...options }),
  put: (endpoint, body, options = {}) => request(endpoint, { method: 'PUT', body, ...options }),
  delete: (endpoint, options = {}) => {
    const { data, ...rest } = options;
    return request(endpoint, { method: 'DELETE', body: data, ...rest });
  },
};